import { create } from 'zustand';
import { bookingApi } from '../api/providerMeApi';
import { logger } from '../utils/logger';
import { todayInTashkent, addDaysToDateStr } from '../utils/dateTz';
import type {
  Booking,
  RangeKey,
  StatusFilterKey,
  BookingsSummary,
  BookingsErrorKind,
  BookingStatus,
  BookingDeclineReason,
} from '../types/provider';
import { PAGE_SIZE } from '../components/provider/bookings/constants';

interface BookingsState {
  bookings: Booking[];
  summary: BookingsSummary | null;
  range: RangeKey;
  statusFilter: StatusFilterKey;
  page: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  actionBookingId: number | null;
  error: BookingsErrorKind | null;
  errorMessage: string | null;

  setRange: (range: RangeKey) => void;
  setStatusFilter: (filter: StatusFilterKey) => void;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  confirmBooking: (id: number) => Promise<void>;
  declineBooking: (id: number, reason: BookingDeclineReason) => Promise<void>;
  completeBooking: (id: number) => Promise<ActionResult>;
  cancelByProvider: (id: number) => Promise<void>;
  markAsPaid: (id: number) => Promise<MarkAsPaidResult>;
  markNoShow: (id: number, reason: string) => Promise<ActionResult>;
  clearError: () => void;
  reset: () => void;
}

export type ActionResult =
  | { ok: true }
  | { ok: false; kind: BookingsErrorKind; message: string };

export type MarkAsPaidResult = ActionResult;

function getDateRange(range: RangeKey): { startDate: string; endDate: string } {
  // Asia/Tashkent (UTC+5) mahalliy sanasi — UTC emas, aks holda yarim tunda
  // (00:00–05:00) "bugun" filtri oldingi kunni so'rab qoladi.
  const today = todayInTashkent();

  if (range === 'today') {
    return { startDate: today, endDate: today };
  }

  const [y, m, d] = today.split('-').map(Number);
  const anchor = new Date(Date.UTC(y, m - 1, d));

  if (range === 'week') {
    // Dushanbadan boshlanadigan hafta. getUTCDay: 0=Yakshanba..6=Shanba.
    const daysSinceMonday = (anchor.getUTCDay() + 6) % 7;
    const start = addDaysToDateStr(today, -daysSinceMonday);
    return { startDate: start, endDate: addDaysToDateStr(start, 6) };
  }

  // month — joriy oyning birinchi va oxirgi kuni
  const mm = String(m).padStart(2, '0');
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return {
    startDate: `${y}-${mm}-01`,
    endDate: `${y}-${mm}-${String(lastDay).padStart(2, '0')}`,
  };
}

function buildSummary(bookings: Booking[]): BookingsSummary {
  return {
    total: bookings.length,
    pending: bookings.filter((b) => b.status === 'PENDING').length,
    confirmed: bookings.filter((b) => b.status === 'CONFIRMED').length,
    completed: bookings.filter((b) => b.status === 'COMPLETED').length,
    canceled: bookings.filter(
      (b) =>
        b.status === 'CANCELED_BY_USER' ||
        b.status === 'CANCELED_BY_SERVICE_PROVIDER'
    ).length,
  };
}

function classifyError(err: unknown): { kind: BookingsErrorKind; message: string } {
  const error = err as {
    response?: { status?: number; data?: { message?: string; code?: string } };
    code?: string;
    message?: string;
  };

  if (!navigator.onLine) {
    return { kind: 'offline', message: 'Internet aloqasi yo\'q' };
  }
  const status = error?.response?.status;
  const code = error?.response?.data?.code;

  if (status === 409 && code === 'BOOKING_PAYMENT_INVALID_TRANSITION') {
    return {
      kind: 'conflict',
      message: "Bekor qilingan bron uchun to'lov belgilanmaydi",
    };
  }
  if (status === 422 && code === 'BOOKING_PAYMENT_NOT_APPLICABLE') {
    return {
      kind: 'unknown',
      message: "Bu bron uchun to'lov belgilash mumkin emas",
    };
  }
  if (status === 403) {
    return { kind: 'unknown', message: "Ruxsat yo'q" };
  }
  if (status === 404) {
    return { kind: 'unknown', message: 'Bron topilmadi' };
  }
  if (status === 400) {
    return { kind: 'unknown', message: "Noto'g'ri so'rov" };
  }
  if (status === 409) {
    return { kind: 'conflict', message: 'Bron holati o\'zgargan. Yangilanmoqda...' };
  }
  if (error?.code === 'ERR_NETWORK' || !error?.response) {
    return { kind: 'network', message: 'Tarmoq xatoligi. Qayta urinib ko\'ring' };
  }
  return {
    kind: 'unknown',
    message: error?.response?.data?.message || 'Xatolik yuz berdi',
  };
}

function getStatusParam(
  filter: StatusFilterKey
): BookingStatus | BookingStatus[] | undefined {
  if (filter === 'ALL') return undefined;
  return filter as BookingStatus;
}

const initialState = {
  bookings: [] as Booking[],
  summary: null as BookingsSummary | null,
  range: 'today' as RangeKey,
  statusFilter: 'CONFIRMED' as StatusFilterKey,
  page: 0,
  hasMore: true,
  isLoading: false,
  isLoadingMore: false,
  actionBookingId: null as number | null,
  error: null as BookingsErrorKind | null,
  errorMessage: null as string | null,
};

export const useBookingsStore = create<BookingsState>((set, get) => ({
  ...initialState,

  setRange: (range) => {
    set({ range, bookings: [], page: 0, hasMore: true, error: null, errorMessage: null, isLoading: true });
    get().refresh();
  },

  setStatusFilter: (filter) => {
    set({ statusFilter: filter, bookings: [], page: 0, hasMore: true, error: null, errorMessage: null, isLoading: true });
    get().refresh();
  },

  refresh: async () => {
    const { range, statusFilter } = get();
    set({ isLoading: true, error: null, errorMessage: null });
    try {
      const dateRange = getDateRange(range);
      const status = getStatusParam(statusFilter);
      const data = await bookingApi.search({
        ...dateRange,
        ...(status !== undefined ? { status } : {}),
        page: 0,
        size: PAGE_SIZE,
      });
      const summary = buildSummary(data.content);
      set({
        bookings: data.content,
        summary,
        page: 0,
        hasMore: !data.last,
        isLoading: false,
      });
      logger.info('Bookings refreshed', { count: data.content.length, range, statusFilter });
    } catch (err: unknown) {
      const { kind, message } = classifyError(err);
      set({ isLoading: false, error: kind, errorMessage: message });
      logger.error('Failed to refresh bookings', { kind });

      // 409: auto refresh once
      if (kind === 'conflict') {
        setTimeout(() => get().refresh(), 1000);
      }
    }
  },

  loadMore: async () => {
    const { range, statusFilter, page, hasMore, isLoadingMore } = get();
    if (!hasMore || isLoadingMore) return;

    set({ isLoadingMore: true });
    try {
      const dateRange = getDateRange(range);
      const status = getStatusParam(statusFilter);
      const nextPage = page + 1;
      const data = await bookingApi.search({
        ...dateRange,
        ...(status !== undefined ? { status } : {}),
        page: nextPage,
        size: PAGE_SIZE,
      });
      set((state) => ({
        bookings: [...state.bookings, ...data.content],
        page: nextPage,
        hasMore: !data.last,
        isLoadingMore: false,
      }));
    } catch (err: unknown) {
      const { kind, message } = classifyError(err);
      set({ isLoadingMore: false, error: kind, errorMessage: message });
      logger.error('Failed to load more bookings', { kind });
    }
  },

  confirmBooking: async (id) => {
    set({ actionBookingId: id });
    try {
      await bookingApi.confirm(id);
      set((state) => {
        const next = state.bookings.map((b) =>
          b.id === id ? { ...b, status: 'CONFIRMED' as const } : b,
        );
        return {
          bookings: next,
          actionBookingId: null,
          summary: buildSummary(next),
        };
      });
      logger.info('Booking confirmed', { id });
    } catch (err: unknown) {
      const { kind, message } = classifyError(err);
      set({ actionBookingId: null, error: kind, errorMessage: message });
      logger.error('Failed to confirm booking', { id, kind });
    }
  },

  declineBooking: async (id, reason) => {
    set({ actionBookingId: id });
    try {
      await bookingApi.decline(id, reason);
      set((state) => {
        const next = state.bookings.map((b) =>
          b.id === id ? { ...b, status: 'CANCELED_BY_SERVICE_PROVIDER' as const } : b,
        );
        return {
          bookings: next,
          actionBookingId: null,
          summary: buildSummary(next),
        };
      });
      logger.info('Booking declined', { id });
    } catch (err: unknown) {
      const { kind, message } = classifyError(err);
      set({ actionBookingId: null, error: kind, errorMessage: message });
      logger.error('Failed to decline booking', { id, kind });
    }
  },

  completeBooking: async (id) => {
    set({ actionBookingId: id });
    try {
      await bookingApi.complete(id);
      set((state) => {
        const next = state.bookings.map((b) =>
          b.id === id ? { ...b, status: 'COMPLETED' as const } : b,
        );
        return {
          bookings: next,
          actionBookingId: null,
          summary: buildSummary(next),
        };
      });
      logger.info('Booking completed', { id });
      return { ok: true };
    } catch (err: unknown) {
      const { kind, message } = classifyError(err);
      set({ actionBookingId: null });
      logger.error('Failed to complete booking', { id, kind });
      if (kind === 'conflict') {
        setTimeout(() => get().refresh(), 1000);
      }
      return { ok: false, kind, message };
    }
  },

  cancelByProvider: async (id) => {
    set({ actionBookingId: id });
    try {
      await bookingApi.cancelByProvider(id);
      set((state) => {
        const updated = state.bookings.map((b) =>
          b.id === id ? { ...b, status: 'CANCELED_BY_SERVICE_PROVIDER' as const } : b
        );
        return { bookings: updated, actionBookingId: null, summary: buildSummary(updated) };
      });
      logger.info('Booking cancelled by provider', { id });
      setTimeout(() => get().refresh(), 800);
    } catch (err: unknown) {
      const { kind, message } = classifyError(err);
      set({ actionBookingId: null, error: kind, errorMessage: message });
      logger.error('Failed to cancel booking', { id, kind });
    }
  },

  markAsPaid: async (id) => {
    set({ actionBookingId: id });
    try {
      const updated = await bookingApi.markAsPaid(id);
      set((state) => {
        const next = state.bookings.map((b) => (b.id === id ? updated : b));
        return {
          bookings: next,
          actionBookingId: null,
          summary: buildSummary(next),
        };
      });
      logger.info('Booking marked as paid', { id });
      return { ok: true };
    } catch (err: unknown) {
      const { kind, message } = classifyError(err);
      set({ actionBookingId: null });
      logger.error('Failed to mark booking as paid', { id, kind });
      if (kind === 'conflict') {
        setTimeout(() => get().refresh(), 1000);
      }
      return { ok: false, kind, message };
    }
  },

  markNoShow: async (id, reason) => {
    set({ actionBookingId: id });
    try {
      await bookingApi.markNoShow(id, reason);
      set((state) => {
        const next = state.bookings.map((b) =>
          b.id === id ? { ...b, status: 'NO_SHOW' as const } : b,
        );
        return {
          bookings: next,
          actionBookingId: null,
          summary: buildSummary(next),
        };
      });
      logger.info('Booking marked as no-show', { id });
      return { ok: true };
    } catch (err: unknown) {
      const { kind, message } = classifyError(err);
      set({ actionBookingId: null });
      logger.error('Failed to mark booking as no-show', { id, kind });
      if (kind === 'conflict') {
        setTimeout(() => get().refresh(), 1000);
      }
      return { ok: false, kind, message };
    }
  },

  clearError: () => set({ error: null, errorMessage: null }),

  reset: () => set({ ...initialState }),
}));
